import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { ROTULO_DO_ELEMENTO } from "@bookdental/schemas";
import { useSetPageHeader } from "@/lib/page-header";
import { usePermissao } from "@/lib/use-permissao";
import { useFeature } from "@/lib/use-feature";
import { useIsMobile } from "@/lib/use-media-query";
import { useConfirm } from "@/lib/confirm";
import { trpc } from "@/lib/trpc";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { PlanoPagoSalas } from "@/components/salas/PlanoPagoSalas";
import type { SalaAgora } from "@/components/salas/use-salas-agora";
import { GradeDaPlanta } from "@/components/salas/planta/GradeDaPlanta";
import { PecaEditavel } from "@/components/salas/planta/PecaEditavel";
import { PaletaDaPlanta } from "@/components/salas/planta/PaletaDaPlanta";
import { useEditorDaPlanta } from "@/components/salas/planta/use-editor-da-planta";

/** Montar a maquete da clínica: salas e outros espaços numa grade, salvos de uma vez. */
export function EditorDaPlanta() {
  useSetPageHeader({ title: "Planta da clínica", subtitle: "Arraste as salas para montar o espaço" }, []);
  const temRecurso = useFeature("salas");
  const podeEditar = usePermissao("settings.manage");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const utils = trpc.useUtils();
  const inicial = trpc.planta.obter.useQuery(undefined, { enabled: temRecurso && podeEditar });
  const salas = (trpc.salas.listar.useQuery(undefined, { enabled: temRecurso && podeEditar }).data ?? []) as SalaAgora[];
  const ed = useEditorDaPlanta(inicial.data);
  const grade = useRef<HTMLDivElement>(null);
  const salvar = trpc.planta.salvar.useMutation({
    onSuccess: () => {
      ed.salvo();
      utils.planta.obter.invalidate();
      toast.success("Planta salva");
    },
    onError: (e) => toast.error(e.message),
  });

  // Fechar a aba com alteração por salvar pede confirmação do navegador.
  useEffect(() => {
    if (!ed.sujo) return;
    const aviso = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", aviso);
    return () => window.removeEventListener("beforeunload", aviso);
  }, [ed.sujo]);

  const voltar = async () => {
    if (ed.sujo && !(await confirm({ title: "Sair sem salvar?", description: "As mudanças na planta vão se perder.", confirmText: "Sair", cancelText: "Continuar editando" }))) return;
    navigate("/salas");
  };

  const embrulho = (filho: React.ReactNode) => <div className="mx-auto w-full max-w-[1400px] space-y-4 pb-10">{filho}</div>;
  if (!temRecurso) return embrulho(<PlanoPagoSalas />);
  if (!podeEditar) return embrulho(<NoAccessBlock description="A planta é montada por quem edita as configurações da clínica." />);
  if (isMobile) return embrulho(<p className="rounded-xl border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-border">Abra no computador para montar a planta. <Link to="/salas" className="font-semibold text-brand-600">Ver as salas</Link></p>);
  const p = ed.planta;
  if (!p) return embrulho(<Loader2 className="mx-auto mt-16 h-5 w-5 animate-spin text-zinc-400" />);

  const nomeDaSala = (id: string) => salas.find((s) => s.id === id)?.nome ?? "Sala";
  const sel = ed.selecao;
  const elemento = sel?.tipo === "elemento" ? p.elementos.find((e) => e.id === sel.id) : undefined;
  const selecionado = sel ? { selecao: sel, nome: sel.tipo === "sala" ? nomeDaSala(sel.id) : (elemento?.rotulo ?? ""), elemento } : null;

  return embrulho(
    <>
      <div className="flex items-center gap-2">
        <button type="button" onClick={voltar} className="inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
          <ArrowLeft className="h-4 w-4" />
          Salas
        </button>
        <span className="flex-1" />
        {ed.sujo && <span className="text-xs text-amber-600">Alterações não salvas</span>}
        <button
          type="button"
          disabled={!ed.sujo || salvar.isPending}
          onClick={() => salvar.mutate(p)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {salvar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Salvar planta
        </button>
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-[240px_1fr]">
        <PaletaDaPlanta
          tamanho={{ colunas: p.colunas, linhas: p.linhas, limites: ed.limitesDoTamanho!, onMudar: ed.redimensionar }}
          foraDaPlanta={salas.filter((s) => !p.salas.some((x) => x.salaId === s.id))}
          selecionado={selecionado}
          onAdicionarSala={(id) => ed.adicionarSala(id) || toast.error("A planta está cheia.")}
          onAdicionarElemento={(t) => ed.adicionarElemento(t) || toast.error("A planta está cheia.")}
          onRenomear={ed.renomear}
          onRemover={ed.remover}
        />
        <div onPointerDown={() => ed.setSelecao(null)}>
          <GradeDaPlanta ref={grade} colunas={p.colunas} linhas={p.linhas} comLinhas>
            {p.elementos.map((e) => (
              <PecaEditavel key={e.id} caixa={e} colunas={p.colunas} linhas={p.linhas} grade={grade} rotulo={e.rotulo || ROTULO_DO_ELEMENTO[e.tipo]}
                selecionada={sel?.tipo === "elemento" && sel.id === e.id} onSelecionar={() => ed.setSelecao({ tipo: "elemento", id: e.id })}
                onPosicionar={(c) => ed.posicionar({ tipo: "elemento", id: e.id }, c)} onRemover={() => ed.remover({ tipo: "elemento", id: e.id })}>
                <span className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-zinc-400 bg-zinc-100 px-1 text-center text-[11px] font-medium text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                  {e.rotulo || ROTULO_DO_ELEMENTO[e.tipo]}
                </span>
              </PecaEditavel>
            ))}
            {p.salas.map((s) => {
              const sala = salas.find((x) => x.id === s.salaId);
              return (
                <PecaEditavel key={s.salaId} caixa={s} colunas={p.colunas} linhas={p.linhas} grade={grade} rotulo={nomeDaSala(s.salaId)}
                  selecionada={sel?.tipo === "sala" && sel.id === s.salaId} onSelecionar={() => ed.setSelecao({ tipo: "sala", id: s.salaId })}
                  onPosicionar={(c) => ed.posicionar({ tipo: "sala", id: s.salaId }, c)} onRemover={() => ed.remover({ tipo: "sala", id: s.salaId })}>
                  <span className="flex h-full w-full items-start gap-1.5 rounded-lg border-2 bg-white p-2 text-xs font-semibold text-zinc-900 dark:bg-card dark:text-foreground" style={{ borderColor: sala?.cor ?? "#a1a1aa" }}>
                    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: sala?.cor ?? "#a1a1aa" }} />
                    <span className="truncate">{nomeDaSala(s.salaId)}</span>
                  </span>
                </PecaEditavel>
              );
            })}
          </GradeDaPlanta>
        </div>
      </div>
    </>,
  );
}
